import { useEffect, useRef } from "react";
import vtkImageData from "vtk.js/Sources/Common/DataModel/ImageData";
import vtkDataArray from "vtk.js/Sources/Common/Core/DataArray";
import vtkActor from "vtk.js/Sources/Rendering/Core/Actor";
import vtkMapper from "vtk.js/Sources/Rendering/Core/Mapper";
import vtkLineSource from "vtk.js/Sources/Filters/Sources/LineSource";
import { calculateDiameters } from "rawmap-diameter-algo";
import Konva from "konva";
import vtkCoordinate from "vtk.js/Sources/Rendering/Core/Coordinate";

/**
 * @param {Object} parentContext
 * @param {number} slicePlaneIndex
 * @param {any} segmentationChanged
 * @param {any} dicomFilesChanged
 * @param {boolean} isVisible
 */
export default function useDiameterLines(
    parentContext,
    slicePlaneIndex,
    segmentationChanged,
    dicomFilesChanged,
    isVisible,
    trigger
) {
    const parentContextRef = useRef(parentContext);
    const maxLineRef = useRef(null);
    const avgLineRef = useRef(null);
    const layerRef = useRef(null);
    const floatingTextRef = useRef(null);
    const cornerTextRef = useRef(null);
    const floatingLabelRef = useRef(null);
    const cornerLabelRef = useRef(null);
    const floatingLabelLineRef = useRef(null);
    const isVisibleRef = useRef(isVisible);

    const templateText = (avg, max) =>
        `Avg. Diameter: ${avg.toFixed(1)} mm\nMax. Diameter: ${max.toFixed(1)} mm`;

    // Keep parent context ref up to date
    useEffect(() => {
        parentContextRef.current = parentContext;
    }, [parentContext]);

    useEffect(() => {
        isVisibleRef.current = isVisible;

        if (cornerLabelRef.current) {
            cornerLabelRef.current.visible(isVisible);
        }
    }, [isVisible]);

    // Create actors and add to renderer
    useEffect(() => {
        const ctx = parentContextRef.current;
        const { renderWindow, renderer, konvaStage, resizeCallbacks } =
            ctx ?? {};
        if (!renderWindow || !renderer || !konvaStage || !resizeCallbacks) {
            return;
        }

        if (
            maxLineRef.current &&
            avgLineRef.current &&
            floatingTextRef.current
        ) {
            return;
        }

        const layer = new Konva.Layer();
        const cornerLabel = new Konva.Label();

        const cornerTag = new Konva.Tag({
            stroke: "red",
            strokeWidth: 1,
            dash: [4, 2],
            fillEnabled: false,
        });

        const cornerText = new Konva.Text({
            text: templateText(NaN, NaN),
            fontFamily: "Open Sans",
            fontSize: 32,
            fill: "yellow",
            shadowColor: "black",
            shadowBlur: 3,
            shadowOffset: { x: 0, y: 0 },
            shadowOpacity: 1,
            shadowForStrokeEnabled: false,
        });

        cornerLabel.add(cornerTag);
        cornerLabel.add(cornerText);

        const floatingLabel = new Konva.Label();

        const floatingTag = new Konva.Tag({
            stroke: "red",
            strokeWidth: 1,
            dash: [4, 2],
            fillEnabled: false,
        });

        const floatingText = new Konva.Text({
            text: "",
            fontFamily: "Open Sans",
            fontSize: 32,
            fill: "yellow",
            shadowColor: "black",
            shadowBlur: 3,
            shadowOffset: { x: 0, y: 0 },
            shadowOpacity: 1,
            shadowForStrokeEnabled: false,
        });

        const floatingLabelLine = new Konva.Line({
            stroke: "red",
            strokeWidth: 1,
            dash: [4, 2],
            dashEnabled: true,
        });

        floatingLabel.add(floatingTag);
        floatingLabel.add(floatingText);

        layer.add(cornerLabel);
        layer.add(floatingLabel);
        layer.add(floatingLabelLine);
        konvaStage.add(layer);
        layer.draw();

        updateCornerLabel(
            cornerLabel,
            konvaStage.width(),
            konvaStage.height(),
            isVisibleRef.current,
        );

        floatingTextRef.current = floatingText;
        cornerTextRef.current = cornerText;
        floatingLabelRef.current = floatingLabel;
        cornerLabelRef.current = cornerLabel;
        layerRef.current = layer;
        floatingLabelLineRef.current = floatingLabelLine;

        const avgLine = mkDiameterLine([1, 0, 0]);
        const maxLine = mkDiameterLine([0, 0, 1]);

        const avgActor = avgLine.actor;
        const maxActor = maxLine.actor;

        avgLineRef.current = avgLine;
        maxLineRef.current = maxLine;

        renderer.addActor(avgActor);
        renderer.addActor(maxActor);

        const camera = renderer.getActiveCamera();
        const sub = camera.onModified(() => {
            updateFloatingLabel(
                renderer,
                renderWindow,
                avgLineRef.current.lineSource,
                floatingLabelRef.current,
                layerRef.current,
                isVisibleRef.current,
                floatingLabelLineRef.current,
            );
        });

        resizeCallbacks.add((width, height) => {
            updateCornerLabel(
                cornerLabelRef.current,
                width,
                height,
                isVisibleRef.current,
            );
            updateFloatingLabel(
                renderer,
                renderWindow,
                avgLineRef.current.lineSource,
                floatingLabelRef.current,
                layerRef.current,
                isVisibleRef.current,
                floatingLabelLineRef.current,
            );
        });

        return () => {
            try {
                renderer.removeActor(avgActor);
                renderer.removeActor(maxActor);

                layerRef.current?.destroy();
                floatingTextRef.current?.destroy();
                cornerTextRef.current?.destroy();
                floatingLabelRef.current?.destroy();
                cornerLabelRef.current?.destroy();
                floatingLabelLineRef.current?.destroy();

                floatingTag.destroy();
                cornerTag.destroy();
            } catch {
                // ignore
            }

            avgLineRef.current = null;
            maxLineRef.current = null;

            layerRef.current = null;
            floatingTextRef.current = null;
            cornerTextRef.current = null;
            floatingLabelRef.current = null;
            cornerLabelRef.current = null;
            floatingLabelLineRef.current = null;

            sub?.unsubscribe?.();
        };
    }, [parentContext]);

    // Update diameter line points
    useEffect(() => {
        const ctx = parentContextRef.current;
        const { renderWindow, renderer, maskMapper, konvaStage } = ctx ?? {};

        const maxActor = maxLineRef?.current?.actor;
        const avgActor = avgLineRef?.current?.actor;

        const hasSlicePlaneIndex = Number.isFinite(slicePlaneIndex);
        const areDepsReady =
            renderWindow &&
            renderer &&
            maskMapper &&
            hasSlicePlaneIndex &&
            maxActor &&
            avgActor &&
            konvaStage;
        if (!areDepsReady) {
            return;
        }

        const volumeData = maskMapper.getInputData();
        if (!volumeData) {
            return;
        }

        const zIndex = Math.round(slicePlaneIndex);
        const imageData = extractZSlice(volumeData, zIndex);

        const diameters = calculateDiameters(imageData, 0);
        const maxLine = diameters?.samples?.length
            ? diameters.samples.reduce((max, current) =>
                current.length > max.length ? current : max,
            )
            : null;
        const avgLine = diameters?.AverageLine ?? null;

        cornerTextRef.current.text(
            templateText(
                diameters.stentPlanningAverage ?? NaN,
                diameters.stentPlanningMax ?? NaN,
            ),
        );
        floatingTextRef.current.text(
            `${diameters.stentPlanningAverage.toFixed(1) ?? 0} mm`,
        );

        const hasLines = !!(avgLine && maxLine);
        setActorsVisibility(
            [maxActor, avgActor],
            isVisibleRef.current && hasLines,
        );
        if (!hasLines) {
            return;
        }

        const maxLineSource = maxLineRef.current.lineSource;
        const avgLineSource = avgLineRef.current.lineSource;

        updateDiameterLineSource(
            maxLineSource,
            { pMinus: maxLine.pMinus, pPlus: maxLine.pPlus },
            zIndex,
        );
        updateDiameterLineSource(
            avgLineSource,
            { pMinus: avgLine.pMinus, pPlus: avgLine.pPlus },
            zIndex,
        );

        updateFloatingLabel(
            renderer,
            renderWindow,
            avgLineRef.current.lineSource,
            floatingLabelRef.current,
            layerRef.current,
            isVisibleRef.current,
            floatingLabelLineRef.current,
        );

        maxActor.setVisibility(isVisibleRef.current);
        avgActor.setVisibility(isVisibleRef.current);

        renderWindow.render();
        layerRef.current?.draw();
    }, [slicePlaneIndex, segmentationChanged, dicomFilesChanged, trigger]);
}

/**
 * @param {vtkActor[]} actors
 * @param {boolean} isVisible
 */
function setActorsVisibility(actors, isVisible) {
    for (const actor of actors) {
        actor.setVisibility(isVisible);
    }
}

/**
 * @param {[number, number, number]} color
 * @returns {{actor: vtkActor, lineSource: vtkLineSource}}
 */
function mkDiameterLine(color) {
    const lineSource = vtkLineSource.newInstance();

    const mapper = vtkMapper.newInstance();
    mapper.setInputConnection(lineSource.getOutputPort());

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.setVisibility(false);

    const property = actor.getProperty();
    property.setLineWidth(2);
    property.setColor(color);

    return { actor: actor, lineSource: lineSource };
}

/**
 * @param {vtkLineSource} lineSource
 * @param {{pMinus: [number, number], pPlus: [number, number]}} linePoints
 * @param {number} zIndex
 */
function updateDiameterLineSource(lineSource, linePoints, zIndex) {
    lineSource.setPoint1(linePoints.pMinus[0], linePoints.pMinus[1], zIndex);
    lineSource.setPoint2(linePoints.pPlus[0], linePoints.pPlus[1], zIndex);
}

/**
 * @param {vtkImageData} volumeData
 * @param {number} sliceIndex
 * @returns {vtkImageData}
 */
function extractZSlice(volumeData, sliceIndex) {
    const [xDim, yDim, zDim] = volumeData.getDimensions();

    if (sliceIndex < 0 || sliceIndex >= zDim) {
        throw new Error("sliceIndex out of bounds");
    }

    const scalars = volumeData.getPointData().getScalars();
    const data = scalars.getData();
    const numberOfComponents = scalars.getNumberOfComponents();

    const sliceSize = xDim * yDim * numberOfComponents;
    const zOffset = sliceIndex * sliceSize;

    const sliceValues = new data.constructor(sliceSize);

    sliceValues.set(data.subarray(zOffset, zOffset + sliceSize));

    const imageData = vtkImageData.newInstance();
    imageData.setDimensions(xDim, yDim, 1);
    imageData.setSpacing(...volumeData.getSpacing());
    imageData.setOrigin(
        ...volumeData
            .getOrigin()
            .map((v, i) =>
                i === 2 ? v + sliceIndex * volumeData.getSpacing()[2] : v,
            ),
    );

    imageData.getPointData().setScalars(
        vtkDataArray.newInstance({
            name: scalars.getName(),
            numberOfComponents: numberOfComponents,
            values: sliceValues,
        }),
    );

    return imageData;
}

/**
 * Update the position and visibility of the corner label.
 * @param {Konva.Label} label
 * @param {number} width
 * @param {number} height
 * @param {boolean} isVisible
 */
function updateCornerLabel(label, width, height, isVisible) {
    const dimensions = label.getClientRect({ skipTransform: false });
    label.position({
        x: width - dimensions.width - 10,
        y: height - dimensions.height - 10,
    });
    label.visible(isVisible);
}

/**
 * Update the position and visibility of the floating label based on the line's position in the renderer.
 * @param {vtkRenderer} renderer
 * @param {vtkRenderWindow} renderWindow
 * @param {vtkLineSource} line
 * @param {Konva.Label} label
 * @param {Konva.Layer} layer
 * @param {boolean} isVisible
 * @param {Konva.Line} labelLine
 */
function updateFloatingLabel(
    renderer,
    renderWindow,
    line,
    label,
    layer,
    isVisible,
    labelLine,
) {
    const hideLabel = () => {
        label.visible(false);
        labelLine.visible(false);
        layer.batchDraw();
    };

    const canvas = renderWindow.getViews()?.[0]?.getCanvas?.();

    if (!(canvas && canvas.width && canvas.height)) {
        hideLabel();
        return;
    }

    const width = canvas.width ?? 0;
    const height = canvas.height ?? 0;

    const firstPointWorld = line.getPoint1();
    const secondPointWorld = line.getPoint2();

    const coordinate = vtkCoordinate.newInstance();
    coordinate.setCoordinateSystemToWorld();

    coordinate.setValue(...firstPointWorld);
    const firstPointDisplay = coordinate.getComputedDisplayValue(renderer);

    coordinate.setValue(...secondPointWorld);
    const secondPointDisplay = coordinate.getComputedDisplayValue(renderer);

    const displayCoordinates = closestToTopRight(
        [firstPointDisplay, secondPointDisplay],
        width,
        height,
    );

    if (!displayCoordinates) {
        hideLabel();
        return;
    }

    const dpr = window.devicePixelRatio || 1;
    const konvaX = displayCoordinates[0] / dpr;
    const konvaY = (height - displayCoordinates[1]) / dpr;

    label.position({ x: konvaX + 40, y: konvaY - label.height() - 40 });
    labelLine.points([konvaX, konvaY, konvaX + 40, konvaY - 40]);

    label.visible(isVisible);
    labelLine.visible(isVisible);
    layer.batchDraw();
}

/**
 * Find the point closest to the top-right corner of the canvas.
 * @param {[number, number][]} points - The points to evaluate
 * @param {number} width - The width of the canvas
 * @param {number} height - The height of the canvas
 */
function closestToTopRight(points, width, height) {
    let bestPoint = null;
    let bestDistance = Infinity;

    points.forEach((point) => {
        const screenX = point[0];
        const screenY = height - point[1];

        const distanceX = width - screenX;
        const distanceY = 0 - screenY;

        const distance = distanceX * distanceX + distanceY * distanceY;

        if (distance < bestDistance) {
            bestDistance = distance;
            bestPoint = point;
        }
    });

    return bestPoint;
}